import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import { dashboardService } from '../services/api';
import { Card, Spinner, Badge } from '../components/common/UI';
import { formatCurrency, formatMonth, getStatusConfig, formatDateTimeUz } from '../utils/format';
import '../components/common/styles.css';

function StatCard({ icon, label, value, accent, delay = 0 }) {
  return (
    <div className="stat-card fade-in" style={{
      '--stat-accent': accent + '22',
      animationDelay: `${delay}ms`,
    }}>
      <div className="stat-icon" style={{ background: accent + '18' }}>
        {icon}
      </div>
      <div className="stat-value">{value ?? <span style={{ fontSize: 20 }}>—</span>}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-primary)', borderRadius: 'var(--radius)',
      border: '1px solid var(--border)', padding: '10px 14px',
      boxShadow: 'var(--shadow-md)', fontSize: 13,
    }}>
      <p style={{ fontWeight: 700, marginBottom: 4 }}>{formatMonth(label)}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' && p.value > 1000
            ? formatCurrency(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

export default function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardService.stats().then(r => r.data),
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spinner size="lg" />
      </div>
    );
  }

  const { employees = {}, salaries = {}, recent_uploads = [], available_months = [] } = data || {};

  // Build chart data from available months
  const chartData = available_months.slice(0, 8).reverse().map(m => ({
    month: m,
    records: 0, // would come from per-month breakdown endpoint
  }));

  return (
    <div>
      {/* Stats grid */}
      <div className="stats-grid">
        <StatCard
          icon="👥" label="Jami xodimlar"
          value={employees.total ?? 0}
          accent="#0ea5e9" delay={0}
        />
        <StatCard
          icon="🤖" label="Botda ro'yxatdan o'tgan"
          value={employees.registered ?? 0}
          accent="#10b981" delay={60}
        />
        
        <StatCard
          icon="💵" label="Jami Hisoblandi"
          value={
            salaries.total_hisoblandi
              ? formatCurrency(salaries.total_hisoblandi)
              : '0 so\'m'
          }
          accent="#3b82f6" delay={180}
        />
        <StatCard
          icon="💳" label="Plastikka o'tkazilgan"
          value={
            salaries.total_plastik
              ? formatCurrency(salaries.total_plastik)
              : '0 so\'m'
          }
          accent="#10b981" delay={240}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Registration status donut */}
        <Card className="fade-in" style={{ animationDelay: '240ms' }}>
          <div className="card-header">
            <span className="card-title">Ro'yxatdan o'tish holati</span>
          </div>
          <div className="card-body">
            {employees.total > 0 ? (
              <>
                <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: 36, fontFamily: 'var(--font-display)',
                      fontWeight: 800, color: 'var(--sky-500)',
                    }}>
                      {Math.round((employees.registered / employees.total) * 100)}%
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Ro'yxatdan o'tgan</div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <ProgressBar
                      label="Ro'yxatdan o'tgan"
                      value={employees.registered}
                      total={employees.total}
                      color="var(--sky-500)"
                    />
                    <ProgressBar
                      label="O'tmagan"
                      value={employees.total - (employees.registered || 0)}
                      total={employees.total}
                      color="var(--slate-300)"
                    />
                  </div>
                </div>
              </>
            ) : (
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Hali xodimlar yo'q</p>
            )}
          </div>
        </Card>

        {/* Available months */}
        <Card className="fade-in" style={{ animationDelay: '300ms' }}>
          <div className="card-header">
            <span className="card-title">Mavjud oylar</span>
            <Badge>{available_months.length} oy</Badge>
          </div>
          <div className="card-body">
            {available_months.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {available_months.slice(0, 12).map((m) => (
                  <div key={m} style={{
                    padding: '5px 12px', borderRadius: 99,
                    background: 'var(--sky-50)',
                    border: '1px solid var(--sky-200)',
                    fontSize: 12, fontWeight: 600, color: 'var(--sky-700)',
                  }}>
                    {m}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                Hali oylik ma'lumotlari yuklangmagan
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* Recent uploads */}
      <Card className="fade-in" style={{ animationDelay: '360ms' }}>
        <div className="card-header">
          <span className="card-title">So'ngi Excel yuklashlar</span>
        </div>
        {recent_uploads.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  {['Fayl', 'Oy', 'Holat', 'Jami', 'Muvaffaq', 'Xato', 'Vaqt'].map(h => (
                    <th key={h} style={{
                      padding: '10px 16px', textAlign: 'left',
                      borderBottom: '2px solid var(--border)',
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                      textTransform: 'uppercase', color: 'var(--text-secondary)',
                      background: 'var(--bg-secondary)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent_uploads.map((log) => {
                  const sc = getStatusConfig(log.status);
                  return (
                    <tr key={log.id}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--sky-50)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                      style={{ transition: 'var(--transition)' }}
                    >
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', maxWidth: 200 }}>
                        <span style={{
                          fontSize: 13, color: 'var(--text-primary)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
                        }}>📄 {log.file_name}</span>
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                        <code style={{
                          fontSize: 12, background: 'var(--sky-50)',
                          padding: '2px 8px', borderRadius: 4, color: 'var(--sky-700)',
                        }}>{log.month}</code>
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                        <Badge color={sc.color} bg={sc.bg}>{sc.label}</Badge>
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)', fontSize: 13 }}>
                        {log.total_rows}
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', color: 'var(--emerald-500)', fontWeight: 600, fontSize: 13 }}>
                        {log.success_rows}
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', color: log.error_rows > 0 ? 'var(--rose-500)' : 'var(--text-muted)', fontWeight: log.error_rows > 0 ? 600 : 400, fontSize: 13 }}>
                        {log.error_rows}
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {formatDateTimeUz(log.uploaded_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
            <p>Hali Excel fayllari yuklanmagan</p>
          </div>
        )}
      </Card>
    </div>
  );
}

function ProgressBar({ label, value, total, color }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ fontWeight: 600, color }}>{value}</span>
      </div>
      <div style={{
        height: 6, background: 'var(--slate-100)', borderRadius: 99, overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: color, borderRadius: 99,
          transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
        }} />
      </div>
    </div>
  );
}
